import React from "react";
import { Step, Section, ManualShell } from "@/pages/manual/ManualKit";
import {
  ShieldCheck, Lock, UserPlus, LogIn, ListChecks, Upload, Send, KeyRound, HelpCircle,
  Users, Award, RefreshCw, Wallet, Stethoscope, Ban, CheckCircle2, GraduationCap,
} from "lucide-react";

export default function ManualUsuario() {
  return (
    <ManualShell
      testid="manual-usuario"
      pdf="socio.pdf"
      badge="Sitio privado y confidencial"
      title="Manual del Socio Postulante"
      intro="Guía paso a paso para solicitar la rebaja de tu cuota anual en el Estadio Israelita Maccabi de manera simple, segura y confidencial."
    >
      <Section id="que-es" icon={ShieldCheck} title="¿Qué es EIM Becas?" subtitle="Para qué sirve esta plataforma">
        <p className="text-sm text-foreground leading-relaxed">
          EIM Becas es el sitio privado donde los socios del Estadio Israelita Maccabi solicitan una
          <strong> rebaja socioeconómica de su cuota anual</strong>. Completas un formulario guiado con los
          antecedentes de tu grupo familiar, adjuntas la documentación de respaldo y envías tu solicitud.
          El <strong>comité de becas</strong> la revisa con criterios objetivos y define el porcentaje de rebaja.
        </p>
        <div className="grid sm:grid-cols-3 gap-3 pt-1">
          <div className="bg-secondary/50 rounded-xl p-4">
            <Lock className="w-5 h-5 text-accent mb-2" />
            <p className="font-semibold text-sm text-primary">Confidencial</p>
            <p className="text-xs text-muted-foreground">Tus datos se guardan cifrados y solo se usan para evaluar tu solicitud.</p>
          </div>
          <div className="bg-secondary/50 rounded-xl p-4">
            <ListChecks className="w-5 h-5 text-accent mb-2" />
            <p className="font-semibold text-sm text-primary">Guiado</p>
            <p className="text-xs text-muted-foreground">Un asistente de 5 pasos que guarda tu avance automáticamente.</p>
          </div>
          <div className="bg-secondary/50 rounded-xl p-4">
            <ShieldCheck className="w-5 h-5 text-accent mb-2" />
            <p className="font-semibold text-sm text-primary">Justo</p>
            <p className="text-xs text-muted-foreground">Revisado por el comité con criterios claros y trazables.</p>
          </div>
        </div>
      </Section>

      <Section id="cuenta" icon={UserPlus} title="1. Crear tu cuenta e ingresar" subtitle="Acceso con tu RUT">
        <Step n="1" title="Iniciar solicitud">
          En la página de inicio presiona <strong>“Iniciar solicitud”</strong>. Se ingresa con tu
          <strong> RUT chileno</strong> (se valida automáticamente el dígito verificador).
        </Step>
        <Step n="2" title="Verificar correo y crear contraseña">
          Ingresa tu correo; recibirás un <strong>código de verificación</strong>. Luego creas tu contraseña
          personal (mínimo 8 caracteres). Cada persona usa su propio RUT; un correo por cuenta.
        </Step>
        <Step n="3" title="Ingresos posteriores">
          Usa <strong>“Ya tengo una solicitud / Ingresar”</strong> con tu RUT y contraseña. Si la olvidas, usa
          <strong> “¿Olvidaste tu contraseña?”</strong> para recibir un enlace de recuperación a tu correo.
        </Step>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-slate-700">
          <strong>Tip:</strong> tu contraseña es personal. No importa si coincide con la de otra persona; solo debe tener al menos 8 caracteres.
        </div>
      </Section>

      <Section id="asistente" icon={ListChecks} title="2. El asistente de 5 pasos" subtitle="Completar y enviar tu solicitud">
        <Step n="1" title="Grupo familiar">
          Datos del titular, cónyuge/pareja e hijos. Si el titular ya está en la base del EIM, el sistema
          <strong> autocompleta la familia</strong>. Para hijos en el Instituto Hebreo se indica si tienen <strong>beca IH</strong>
          (ver el recuadro sobre la autorización más abajo).
        </Step>
        <Step n="2" title="Ingresos">
          Se declaran los ingresos del grupo familiar. Puedes adjuntar respaldos; el sistema apoya la lectura
          con IA para comparar lo declarado con los documentos.
        </Step>
        <Step n="3" title="Gastos extraordinarios">
          Se registran únicamente los <strong>gastos extraordinarios, recurrentes e inevitables</strong> del hogar,
          organizados en 5 áreas. Revisa el recuadro de abajo para saber <strong>qué sí y qué no</strong> se considera.
        </Step>
      </Section>

      <Section id="ingreso-bruto" icon={Wallet} title="Importante: el análisis se hace con el sueldo BRUTO" subtitle="Cómo se declaran los ingresos">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 text-sm text-slate-700">
          <p className="leading-relaxed">
            Todo el análisis de tu solicitud se realiza con el <strong>ingreso bruto</strong> (antes de descuentos
            previsionales, de salud e impuestos), <strong>no</strong> con el líquido. Esto es porque las tablas de
            evaluación fueron confeccionadas por una asistente social <strong>partiendo desde el monto bruto</strong>.
          </p>
          <p className="leading-relaxed mt-2">
            Declara cada ingreso en su valor bruto y adjunta los respaldos (liquidaciones de sueldo, boletas de honorarios,
            pensiones, etc.). Si una persona no tiene documentos, puedes indicarlo y explicar el motivo.
          </p>
        </div>
      </Section>

      <Section id="gastos-extra" icon={Stethoscope} title="Qué se considera un gasto extraordinario" subtitle="Para completar bien el paso de Gastos">
        <p className="text-sm text-foreground leading-relaxed">
          Un gasto extraordinario es aquel <strong>recurrente e inevitable</strong> que afecta de forma significativa
          la economía familiar y que <strong>no</strong> corresponde a un gasto normal del hogar. Se agrupan en estas áreas:
        </p>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-sm text-emerald-900">
          <p className="font-semibold flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4" /> Sí se consideran</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Gastos médicos extraordinarios:</strong> tratamientos, medicamentos permanentes, terapias y gastos de salud recurrentes no cubiertos.</li>
            <li><strong>Necesidades especiales / discapacidad:</strong> terapias, cuidadores, educación diferencial u otros gastos por discapacidad.</li>
            <li><strong>Vivienda adicional (separación/divorcio):</strong> mantención de una segunda vivienda. Se descuenta el arriendo de la segunda vivienda (no la principal), con contrato de arriendo o el dividendo si es comprada. Máximo a descontar: <strong>20% del ingreso</strong>, incluyendo cuentas y gastos comunes.</li>
            <li><strong>Apoyo a familiares en necesidad:</strong> ayuda económica a padres u otros familiares dependientes.</li>
            <li><strong>Otros gastos extraordinarios recurrentes e inevitables</strong> que no encajen en las áreas anteriores.</li>
          </ul>
          <p className="text-xs mt-2">Cada gasto debe respaldarse con comprobante (boletas, facturas, certificados o transferencias).</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-900">
          <p className="font-semibold flex items-center gap-2 mb-2"><Ban className="w-4 h-4" /> No se consideran</p>
          <p className="leading-relaxed">
            Los costos normales de una familia, por ejemplo: dividendo o arriendo de la <strong>vivienda principal</strong>,
            alimentación, seguros (salud, vida, vehículos), educación/colegiatura regular, servicios básicos (luz, agua,
            gas, internet, teléfono), transporte y combustible, vehículos, vacaciones y viajes, y compras, inversiones o
            créditos de consumo por decisiones personales.
          </p>
        </div>
      </Section>

      <Section id="beca-ih" icon={GraduationCap} title="Beca del Instituto Hebreo (IH)" subtitle="Autorización para consultar con la asistente social del colegio">
        <p className="text-sm text-foreground leading-relaxed">
          Si tu familia tiene hijos con <strong>beca del Instituto Hebreo</strong>, esa beca puede considerarse dentro de la
          evaluación de tu cuota EIM. Para hacerlo, necesitamos tu autorización.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900">
          <p className="leading-relaxed">
            Al postular se te pedirá <strong>autorizar expresamente</strong> que el comité de becas del EIM consulte tu caso
            con la <strong>asistente social del Instituto Hebreo</strong>, con el único fin de verificar el porcentaje de beca IH.
          </p>
          <p className="leading-relaxed mt-2">
            <strong>Si no autorizas esta consulta, la beca IH no se considerará</strong> en la evaluación de tu solicitud.
            La autorización es voluntaria y queda registrada como constancia.
          </p>
        </div>
      </Section>

      <Section id="pasos-finales" icon={ListChecks} title="3. Revisión y envío" subtitle="Últimos pasos del asistente">
        <Step n="4" title="Revisión">
          Un resumen de todo lo ingresado para revisar y corregir antes de enviar.
        </Step>
        <Step n="5" title="Envío">
          Al enviar, tu solicitud queda <strong>“En revisión”</strong> y recibirás un correo de confirmación.
          El avance se guarda solo, así que puedes salir y continuar después.
        </Step>
      </Section>

      <Section id="documentos" icon={Upload} title="3. Documentos y estados" subtitle="Qué adjuntar y cómo seguir tu solicitud">
        <p className="text-sm text-foreground leading-relaxed">
          Puedes subir <strong>varios documentos</strong> de respaldo (liquidaciones, cartolas, certificados, etc.).
          Se almacenan de forma segura y privada. Verás una <strong>recomendación de porcentaje</strong> orientativa,
          pero la <strong>decisión final siempre la toma el comité</strong>.
        </p>
        <div className="bg-secondary/50 rounded-xl p-4 text-sm">
          <p className="font-semibold text-primary flex items-center gap-2"><Send className="w-4 h-4" /> Estados de tu solicitud</p>
          <ul className="list-disc pl-5 mt-2 text-muted-foreground space-y-1">
            <li><strong>Borrador:</strong> aún no enviada.</li>
            <li><strong>En validación de ingreso:</strong> si aún no estás en la base de socios EIM, revisamos tu solicitud antes de continuar.</li>
            <li><strong>En revisión:</strong> enviada, esperando al comité. Si eres socio de la base, puedes usar las instalaciones mientras te evaluamos.</li>
            <li><strong>Información solicitada:</strong> el comité te pidió antecedentes adicionales (te avisamos por correo).</li>
            <li><strong>Procesada:</strong> el comité definió tu porcentaje final de rebaja.</li>
            <li><strong>En cuarentena:</strong> tu ingreso no fue aceptado; puedes continuar indicando 3 socios que te recomienden.</li>
          </ul>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-amber-800">
          <strong>Importante:</strong> si el comité necesita más antecedentes, te llegará un aviso para completar la <strong>información adicional</strong>.
        </div>
      </Section>

      <Section id="ingreso-validacion" icon={Users} title="4. Si aún no estás en la base de socios EIM" subtitle="Validación de ingreso y recomendadores">
        <p className="text-sm text-foreground leading-relaxed">
          Si tu RUT ya figura como socio del EIM, al enviar tu solicitud tienes <strong>acceso automático</strong> a
          las instalaciones mientras se evalúa. Si <strong>no estás en la base</strong>, tu solicitud queda
          <strong> “En validación de ingreso”</strong> y el comité debe dar el visto bueno antes de continuar.
        </p>
        <Step n="1" title="Si el comité no acepta tu ingreso">
          Te invitaremos por correo a indicar el <strong>nombre, RUT y correo de 3 socios del EIM que te recomienden</strong>.
        </Step>
        <Step n="2" title="Segunda revisión">
          Con esos datos, el comité realiza una <strong>segunda revisión</strong> de tu ingreso y te avisa por correo la decisión.
          Cada uno de los 3 socios recibirá un <strong>correo para confirmar</strong> que te recomienda.
        </Step>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-slate-700">
          <strong>Tip:</strong> ingresa a tu solicitud y usa el botón “Ingresar 3 socios recomendantes” para completar esta información.
        </div>
      </Section>

      <Section id="resultado" icon={Award} title="5. Después del resultado" subtitle="Aceptar, rechazar o reevaluar">
        <p className="text-sm text-foreground leading-relaxed">
          Cuando tu solicitud queda <strong>Procesada</strong>, verás el porcentaje aprobado. Si EIM ofrece un
          porcentaje distinto al que solicitaste, podrás elegir:
        </p>
        <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
          <li><strong>Aceptar</strong> el porcentaje ofrecido.</li>
          <li><strong>Rechazar</strong> (el proceso termina sin acuerdo).</li>
          <li><strong>Solicitar reevaluación</strong>, aportando información nueva y documentación de respaldo.</li>
        </ul>
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-amber-800 flex items-start gap-2">
          <RefreshCw className="w-4 h-4 mt-0.5 shrink-0" />
          <span>La <strong>reevaluación exige adjuntar al menos un documento nuevo</strong>. Al enviarla, tu solicitud vuelve a “En revisión” para que el comité la analice otra vez.</span>
        </div>
      </Section>

      <Section id="soporte" icon={HelpCircle} title="6. Preguntas frecuentes y soporte">
        <div className="space-y-3 text-sm">
          <div>
            <p className="font-semibold text-primary flex items-center gap-2"><KeyRound className="w-4 h-4" /> ¿Puedo usar la misma contraseña que otra persona?</p>
            <p className="text-muted-foreground">Sí. La contraseña es personal; solo debe tener mínimo 8 caracteres.</p>
          </div>
          <div>
            <p className="font-semibold text-primary flex items-center gap-2"><LogIn className="w-4 h-4" /> Olvidé mi contraseña</p>
            <p className="text-muted-foreground">En la pantalla de ingreso usa “¿Olvidaste tu contraseña?” para recibir un enlace de recuperación.</p>
          </div>
          <div>
            <p className="font-semibold text-primary">¿Puedo pausar y seguir después?</p>
            <p className="text-muted-foreground">Sí. El asistente guarda tu avance automáticamente; puedes cerrar sesión y continuar cuando quieras.</p>
          </div>
          <div>
            <p className="font-semibold text-primary">¿Dudas o problemas?</p>
            <p className="text-muted-foreground">Escríbenos a <strong>becas@eimbecas.cl</strong>.</p>
          </div>
        </div>
      </Section>
    </ManualShell>
  );
}
